<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import FormattedContent from './Text/FormattedContent.vue';
import ExpandableContent from './Text/ExpandableContent.vue';
import TranslationToggle from '../TranslationToggle.vue';
import { useTranslations } from 'dashboard/composables/useTranslations';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import HiddenMediaNotice from '../HiddenMediaNotice.vue';
import { useMediaVisibility } from '../useMediaVisibility.js';

const { t } = useI18n();

const {
  filteredCurrentChatAttachments,
  attachments,
  content,
  contentAttributes,
  id,
  conversationId,
} = useMessageContext();
const { hideMedia, isUpdatingVisibility } = useMediaVisibility({
  id,
  conversationId,
});
const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);
const renderOriginal = ref(false);
const caption = computed(() =>
  hasTranslations.value && !renderOriginal.value
    ? translationContent.value
    : content.value
);

const attachment = computed(() => {
  return attachments.value[0];
});

// Mídia marcada como imprópria: fica oculta até o agente pedir para ver (só para ele).
const isRevealed = ref(false);
const hiddenMedia = computed(() => contentAttributes.value?.hiddenMedia);
const isMediaHidden = computed(() => !!hiddenMedia.value && !isRevealed.value);
watch(
  () => hiddenMedia.value?.hiddenAt,
  () => {
    isRevealed.value = false;
  }
);

const isCompactImage = computed(() => {
  const { contentType = '', extension = '' } = attachment.value || {};
  return (
    ['image/gif', 'image/webp'].includes((contentType || '').toLowerCase()) ||
    ['gif', 'webp'].includes((extension || '').toLowerCase().replace(/^\./, ''))
  );
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const isDownloading = ref(false);

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};
</script>

<template>
  <BaseBubble
    class="p-1"
    meta-padding-class="px-2 pb-1"
    :class="isCompactImage ? '!max-w-[208px]' : '!max-w-[288px]'"
    data-bubble-name="image"
  >
    <HiddenMediaNotice
      v-if="isMediaHidden"
      :hidden-media="hiddenMedia"
      @reveal="isRevealed = true"
    />
    <div
      v-else-if="hasError"
      class="flex items-center gap-1 text-center rounded-lg"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoaded"
      class="relative group cursor-pointer rounded-lg overflow-hidden"
      @click="showGallery = true"
    >
      <img
        class="skip-context-menu h-auto w-auto max-w-full object-contain"
        :class="{
          'max-h-[200px]': isCompactImage,
          'max-h-[280px]': !isCompactImage,
        }"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button
          v-if="hiddenMedia"
          xs
          solid
          slate
          icon="i-hugeicons-view-off-slash"
          class="opacity-60"
          :title="t('CONVERSATION.HIDDEN_MEDIA.HIDE_AGAIN')"
          :aria-label="t('CONVERSATION.HIDDEN_MEDIA.HIDE_AGAIN')"
          @click.stop="isRevealed = false"
        />
        <Button
          v-else
          xs
          solid
          slate
          icon="i-hugeicons-view-off-slash"
          class="opacity-60"
          :title="t('CONVERSATION.CONTEXT_MENU.HIDE_MEDIA')"
          :aria-label="t('CONVERSATION.CONTEXT_MENU.HIDE_MEDIA')"
          :is-loading="isUpdatingVisibility"
          :disabled="isUpdatingVisibility"
          @click.stop="hideMedia"
        />
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
    <div v-if="caption" class="px-2 pt-2">
      <ExpandableContent>
        <FormattedContent :content="caption" />
      </ExpandableContent>
      <TranslationToggle
        v-if="hasTranslations"
        :showing-original="renderOriginal"
        @toggle="renderOriginal = !renderOriginal"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery && !isMediaHidden"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
